import React, { useEffect, useState, useMemo } from "react";
import DefaultLayout from "../../Layouts/DefaultLayout";
import { useStoreContext } from "../../Contexts/StoreContext";
import DefaultLayoutProfile from "../../Layouts/DefaultLayoutProfile";
import CardBoxDefault from "../../Components/Layout/CardBoxDefault";
import CustomerService from "../../Services/CustomerService";
import { useOrderTypeContext } from "../../Contexts/OrderTypeContext";
import { useLoginContext } from "../../Contexts/LoginContext";
import { TbStar, TbCalendar, TbArrowsLeftRight, TbArrowUpRight, TbArrowDownLeft } from "react-icons/tb";
import { useLayoutContext } from "../../Contexts/LayoutContext";

const ProfileFidelity = () => {
    document.title = "Pontos de Fidelidade";

    const { currentStore } = useStoreContext();
    const { orderType } = useOrderTypeContext();
    const { user, setUser } = useLoginContext();
    const { company } = useLayoutContext();
    
    const [loading, setLoading] = useState<boolean>(false);
    const [fidelityExpireInfo, setFidelityExpireInfo] = useState<any>(null);
    const [visibleItems, setVisibleItems] = useState(5);

    const customerService = useMemo(() => new CustomerService(orderType), [orderType]);

    const loadData = async () => {
        try {
            setLoading(true);
            const data = await customerService.getFidelityExpireInfo();
            setFidelityExpireInfo(data);

            // Also refresh main info to get the latest points extract
            const mainInfo = await customerService.getMainInfo();
            if (mainInfo && mainInfo.profile) {
                setUser(mainInfo.profile);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const extract = user?.points_extract || [];
    const paginatedExtract = extract.slice(0, visibleItems);
    const hasMore = visibleItems < extract.length;

    const availablePoints = Number(user?.all_points ?? (typeof user?.fidelity_points === 'number' ? user.fidelity_points : 0));
    const currencyName = company?.fidelity?.currency_name || "Pontos";

    // Assuming last redemption is the last 'debit' in extract
    const lastRedemption = extract.find((item: any) => item.type === 'debit');

    if (!company?.fidelity?.active) return null;

    return (
        <DefaultLayout showHeader={currentStore}>
            <DefaultLayoutProfile>
                <div className="flex flex-col gap-6">
                    {/* Header Section */}
                    <div className="flex flex-col md:flex-row gap-4">
                        {/* Main Balance */}
                        <CardBoxDefault className="flex-1 !flex flex-col items-center justify-center p-8 bg-white">
                            <div className="flex flex-col items-center justify-center w-full text-center">
                                <h2 className="text-gray-500 font-medium mb-1">Saldo {currencyName}</h2>
                                <p className="text-gray-400 text-sm mb-4">disponível para troca</p>
                                <div className="w-16 h-1 bg-custom-primary mb-6"></div>
                                <TbStar className="text-4xl mb-2 text-custom-primary" />
                                <div className="flex items-center justify-center gap-2">
                                    <span className="text-5xl font-bold">{availablePoints}</span>
                                    <span className="text-2xl font-medium text-gray-500">pts</span>
                                </div>
                            </div>
                        </CardBoxDefault>

                        {/* Side Info Cards */}
                        <div className="flex flex-col gap-4 md:w-48">
                            <CardBoxDefault className="flex-1 !flex flex-col items-center justify-center p-4 bg-gray-50/50 border-none">
                                <div className="flex flex-col items-center justify-center w-full text-center">
                                    <TbCalendar className="text-gray-400 text-xl mb-1" />
                                    <div className="text-gray-400 text-[10px] uppercase mb-1 leading-tight flex flex-col items-center">
                                        <span>expira em</span>
                                        <span>30 dias</span>
                                    </div>
                                    <span className="text-lg font-bold text-gray-700">{fidelityExpireInfo?.expiring || 0} pts</span>
                                </div>
                            </CardBoxDefault>

                            <CardBoxDefault className="flex-1 !flex flex-col items-center justify-center p-4 bg-gray-50/50 border-none">
                                <div className="flex flex-col items-center justify-center w-full text-center">
                                    <TbArrowsLeftRight className="text-gray-400 text-xl mb-1" />
                                    <p className="text-gray-400 text-[10px] uppercase mb-1 leading-tight">Última troca</p>
                                    <span className="text-lg font-bold text-gray-700">{lastRedemption ? `${lastRedemption.amount} pts` : '0 pts'}</span>
                                </div>
                            </CardBoxDefault>
                        </div>
                    </div>

                    {/* Statement Table */}
                    <div className="flex flex-col gap-2">
                        {/* Table Header */}
                        <div className="grid grid-cols-3 px-6 py-4 text-gray-500 font-medium text-sm">
                            <div>Pedido</div>
                            <div className="text-center">Data</div>
                            <div className="text-right">Pontos</div>
                        </div>

                        {/* Statement Rows */}
                        <div className="flex flex-col gap-2">
                            {paginatedExtract.length > 0 ? (
                                paginatedExtract.map((item: any, index: number) => (
                                    <div key={index} className="grid grid-cols-3 items-center px-6 py-4 bg-gray-50/50 rounded-xl">
                                        <div className="font-medium text-gray-600">#{item.pedido}</div>
                                        <div className="text-center text-gray-400 text-sm">
                                            {new Date(item.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                                        </div>
                                        <div className="flex items-center justify-end gap-2">
                                            {item.type === 'credit' ? (
                                                <TbArrowUpRight className="text-green-500" />
                                            ) : (
                                                <TbArrowDownLeft className="text-red-500" />
                                            )}
                                            <span className={`font-bold ${item.type === 'credit' ? 'text-gray-600' : 'text-gray-600'}`}>
                                                {item.amount} pts
                                            </span>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="p-8 text-center text-gray-400 bg-gray-50/30 rounded-xl border border-dashed border-gray-200">
                                    Nenhuma movimentação encontrada
                                </div>
                            )}
                        </div>

                        {/* Load More Button */}
                        {hasMore && (
                            <button
                                onClick={() => setVisibleItems(prev => prev + 5)}
                                className="w-full mt-4 py-4 bg-custom-primary hover:bg-custom-primary-hover text-white font-bold rounded-lg transition-colors uppercase tracking-wider text-sm"
                                disabled={loading}
                            >
                                {loading ? 'Carregando...' : 'Carregar mais'}
                            </button>
                        )}
                    </div>
                </div>
            </DefaultLayoutProfile>
        </DefaultLayout>
    );
};

export default ProfileFidelity;
